import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { login } from "../../shared/_services/auth.services";
import { setChallengeToken } from "../../shared/_helpers/otpChallenge";
import { clearUnconfirmedPaymentNotice, takeUnconfirmedPaymentNotice } from "../../shared/_helpers/paymentErrors";
import { Button, Field as UiField, Input } from "../ui";
import { AuthCover, AuthHeading, PasswordInput } from "./AuthCover";
import { WELCOME_START_TRIAL } from "../onboarding/onboardingCopy";

// Exported for tests: which screen a client lands on after login is decided
// entirely here, from fields whose availability the backend has been changing,
// and reaching it through a render would mean standing up Formik, the OTP send
// and the router.
export function getVerifyCodeRouteForUser(user) {
  const userType = user?.userType;

  if (userType === "advisor") {
    return { sessionFrom: "login-advisor", stateFrom: "login-advisor" };
  }

  if (userType === "super-admin" || userType === "admin") {
    return { sessionFrom: "login-admin", stateFrom: "login-admin" };
  }

  if (userType === "client") {
    // Pooled inbox SP5 (owner item 20; spec § Removals, the SP4 seam): every
    // client lands by their stage (getLandingRouteForUser). The old
    // "no-package" and "login-canclledPlan" branches guessed from
    // selected_plan / payment_status; a lapsed or unpaid client now lands on
    // the dashboard, whose notice says what to do.
    return { sessionFrom: "login", stateFrom: "login" };
  }

  return null;
}

function persistCurrentUser(data) {
  const payload = data?.data?.data;

  // Backend SEC-114: this response no longer carries a session token — the
  // password step only proves the password, and /otp-verify mints the session.
  // `token` stays in the persisted shape for the guards that read it; it is
  // simply undefined until then. The challenge token that redeems the code goes
  // to sessionStorage rather than into the persisted identity: it is scoped to
  // this tab's flow and is worthless once spent.
  setChallengeToken(payload?.challengeToken);

  const { challengeToken: _challengeToken, ...user } = payload || {};
  const currentUser = { user, token: user?.token };
  window.localStorage.setItem("currentUser", JSON.stringify(currentUser));
  return currentUser;
}

function resolveLoginErrorMessage(data) {
  if (data?.response?.status === 404) {
    return data?.response?.data?.error?.message;
  }
  return data?.response?.data?.message;
}


const Login = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);

  // A purchase 401 force-logs-out mid-payment; show the contact-support copy
  // that the reload would otherwise have dropped (#88).
  useEffect(() => {
    const notice = takeUnconfirmedPaymentNotice();
    if (notice) {
      enqueueSnackbar(notice, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthentication = async ({ email, password }, setSubmitting) => {
    let lowerCaseEmail = email.toLowerCase();
    const deviceType = "web";
    const data = await login({ email: lowerCaseEmail, password, deviceType });
    if (data.status === 200) {
      clearUnconfirmedPaymentNotice();
      const currentUser = persistCurrentUser(data);
      const verifyRoute = getVerifyCodeRouteForUser(currentUser?.user);

      if (verifyRoute) {
        sessionStorage.setItem("verifyCodeFrom", verifyRoute.sessionFrom);
        navigate("/verify-code", { state: { from: verifyRoute.stateFrom } });
      }
      return;
    }

    enqueueSnackbar(resolveLoginErrorMessage(data), {
      variant: 'error',
      anchorOrigin: { horizontal: 'right', vertical: 'top' },
    });
    setSubmitting(false);
  };

  const initialValues = {
    password: "",
    email: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required.")
      .test(
        "contains-at-and-dot",
        "Email must contain '@' and '.'",
        (value) => {
          if (!value) return false; // Required validation
          return value.includes("@") && value.includes(".");
        }
      )
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
    password: Yup.string().required("Password is required"),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    handleAuthentication(values, setSubmitting);
  };

  return (
    <AuthCover
      art={
        <div className="tw-grid tw-gap-4">
          <video
            className="tw-w-full tw-max-w-sm tw-rounded-lg max-rail:tw-hidden"
            src={`${process.env.REACT_APP_BACKEND}/uploads/WelcometoAstraStars.mp4`}
            controls
            loop
            autoPlay
            muted
            preload="metadata"
          ></video>
          <h2 className="tw-m-0 tw-max-w-sm tw-text-3xl tw-font-semibold tw-text-text">Financial Guidance Built Around You</h2>
          <p className="tw-m-0 tw-max-w-md tw-text-text">Work directly with a dedicated financial advisor who understands your goals — supported by modern tools that keep your plan organized, clear, and always accessible.</p>
          <small className="max-rail:tw-hidden tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest">• Personal Advisor • Secure Platform • Smart Insights</small>
        </div>
      }
    >
      <AuthHeading title="Login" lede="Glad you’re back.!" />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <UiField label="Email Address" error={touched.email && errors.email}>
              <Field
                as={Input}
                type="email"
                id="email"
                name="email"
                placeholder="Email Address"
              />
            </UiField>
            <UiField label="Password" error={touched.password && errors.password}>
              <Field
                as={PasswordInput}
                id="password"
                name="password"
                placeholder="Password"
                shown={showPassword}
                onToggle={() => {
                  setShowPassword(!showPassword);
                }}
              />
            </UiField>
            <div className="tw-mb-6 tw-text-right">
              <NavLink to="/forgot-password" className="tw-text-sm tw-font-bold tw-text-link">
                Forgot Password?
              </NavLink>
            </div>
            <Button type="submit" className="tw-w-full" disabled={isSubmitting}>
              {isSubmitting ? "Loggin in..." : "Login"}
            </Button>
            <p className="tw-mt-6 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-6 tw-text-center tw-text-sm tw-text-text-muted">
              Don't have an account?{" "}
              <NavLink className="tw-font-bold tw-text-link" to="/signup">
                {WELCOME_START_TRIAL}
              </NavLink>
            </p>
          </Form>
        )}
      </Formik>
    </AuthCover>
  );
};

export default Login;
